import * as XLSX from 'xlsx';

/**
 * Translate submission status to display text
 */
const translateStatus = (status, language) => {
    const statusMap = {
        new: { en: 'New', ar: 'جديد' },
        read: { en: 'Read', ar: 'مقروء' },
        replied: { en: 'Replied', ar: 'تم الرد' },
        pending_department: { en: 'Pending Department', ar: 'بانتظار القسم' },
        department_replied: { en: 'Department Replied', ar: 'رد القسم' },
        closed: { en: 'Closed', ar: 'مغلق' },
    };
    const entry = statusMap[status];
    if (entry) return language === 'ar' ? entry.ar : entry.en;
    return status || '-';
};

/**
 * Get translated option label
 * @param {Object} field - Field definition
 * @param {string} value - Option value
 * @param {string} language - Language ('en' or 'ar')
 */
const getOptionLabel = (field, value, language) => {
    if (!field.options || !Array.isArray(field.options)) {
        return value;
    }

    const option = field.options.find(opt => opt.value === value || opt.label_en === value || opt.label_ar === value);
    if (option) {
        return language === 'ar' ? (option.label_ar || option.label_en) : (option.label_en || option.label_ar);
    }

    return value;
};

/**
 * Export submissions to Excel file
 * @param {Array} submissions - Array of submission objects
 * @param {Array} formFields - Array of form field definitions
 * @param {string} language - Current language ('en' or 'ar')
 * @param {Array|null} selectedFields - Optional array of field IDs to include (null = all)
 * @param {string} filename - Optional custom filename
 * @param {Array|null} selectedSystemColumns - Optional array of system column IDs to include (null = all)
 */
export const exportSubmissionsToExcel = (submissions, formFields = [], language = 'en', selectedFields = null, filename = null, selectedSystemColumns = null) => {
    if (!submissions || submissions.length === 0) {
        alert(language === 'ar' ? 'لا توجد بيانات للتصدير' : 'No data to export');
        return;
    }

    // If no form fields provided, try to extract from first submission
    let fieldsToUse = formFields;
    if ((!fieldsToUse || fieldsToUse.length === 0) && submissions[0]) {
        try {
            // Try to get form fields from submission object
            if (submissions[0].form_fields) {
                fieldsToUse = typeof submissions[0].form_fields === 'string'
                    ? JSON.parse(submissions[0].form_fields)
                    : submissions[0].form_fields;
            }
        } catch (e) {
            console.error('Error parsing form fields:', e);
        }
    }

    // Filter fields by selection if provided
    if (selectedFields && selectedFields.length > 0) {
        fieldsToUse = fieldsToUse.filter(f => selectedFields.includes(f.id || f.name));
    }

    // Helper to check if a system column is selected
    const isSystemColSelected = (colId) => selectedSystemColumns === null || selectedSystemColumns.includes(colId);

    // Prepare data for Excel
    const data = submissions.map(submission => {
        const row = {};

        // Add metadata columns (only if selected)
        if (isSystemColSelected('submission_id')) {
            row[language === 'ar' ? 'رقم التقديم' : 'Submission ID'] = submission.submission_id || submission.id;
        }
        if (isSystemColSelected('form_title')) {
            row[language === 'ar' ? 'النموذج' : 'Form'] = language === 'ar'
                ? (submission.form_title_ar || submission.form_title || submission.title || '-')
                : (submission.form_title || submission.title || '-');
        }
        if (isSystemColSelected('status')) {
            row[language === 'ar' ? 'الحالة' : 'Status'] = translateStatus(submission.status, language);
        }
        if (isSystemColSelected('created_at')) {
            row[language === 'ar' ? 'تاريخ التقديم' : 'Submitted At'] = submission.created_at
                ? new Date(submission.created_at).toLocaleString(language === 'ar' ? 'ar-SY' : 'en-US')
                : '-';
        }
        if (isSystemColSelected('responded_by')) {
            // Get responded by using correct API field names
            let respondedBy = '-';
            if (submission.responded_by_name_en || submission.responded_by_name_ar) {
                respondedBy = language === 'ar'
                    ? (submission.responded_by_name_ar || submission.responded_by_name_en)
                    : (submission.responded_by_name_en || submission.responded_by_name_ar);
            } else if (submission.status === 'replied' || submission.status === 'department_replied') {
                respondedBy = submission.read_by_name_en || submission.read_by_name_ar || '-';
            }
            row[language === 'ar' ? 'الرد من' : 'Responded By'] = respondedBy;
        }
        if (isSystemColSelected('full_name')) {
            row[language === 'ar' ? 'الاسم الكامل' : 'Full Name'] = submission.end_user_name || '-';
        }

        // Parse submission data
        let submissionData = {};
        try {
            submissionData = typeof submission.data === 'string'
                ? JSON.parse(submission.data)
                : (submission.data || {});
        } catch (e) {
            console.error('Error parsing submission data:', e);
        }

        // Add form field data
        if (fieldsToUse && fieldsToUse.length > 0) {
            fieldsToUse.forEach(field => {
                // Skip repeater type fields from main level (they'll be handled separately)
                if (field.type === 'repeater') {
                    const label = language === 'ar' ? (field.label_ar || field.label_en) : (field.label_en || field.label_ar);
                    const value = submissionData[field.id];

                    if (Array.isArray(value) && value.length > 0) {
                        // Format repeater data as readable text
                        const repeaterText = value.map((item, idx) => {
                            const itemFields = Object.entries(item)
                                .map(([key, val]) => {
                                    // Find the field definition to get proper label
                                    const subField = field.fields?.find(f => f.id === key);
                                    const subLabel = subField
                                        ? (language === 'ar' ? (subField.label_ar || subField.label_en) : (subField.label_en || subField.label_ar))
                                        : key;

                                    // Translate option values for sub-fields
                                    let translatedVal = val;
                                    if (subField && (subField.type === 'select' || subField.type === 'radio')) {
                                        translatedVal = getOptionLabel(subField, val, language);
                                    }

                                    return `${subLabel}: ${translatedVal}`;
                                })
                                .join(', ');
                            return `[${idx + 1}] ${itemFields}`;
                        }).join(' | ');
                        row[label] = repeaterText;
                    } else {
                        row[label] = '-';
                    }
                } else {
                    const label = language === 'ar' ? (field.label_ar || field.label_en) : (field.label_en || field.label_ar);
                    const value = submissionData[field.id];

                    if (field.type === 'file') {
                        row[label] = value ? (typeof value === 'string' ? value : value.name || 'File uploaded') : '-';
                    } else if (field.type === 'checkbox' && Array.isArray(value)) {
                        // Translate checkbox options
                        const translatedValues = value.map(v => getOptionLabel(field, v, language));
                        row[label] = translatedValues.join(', ');
                    } else if (field.type === 'select' || field.type === 'radio') {
                        // Translate select/radio option
                        row[label] = value ? getOptionLabel(field, value, language) : '-';
                    } else if (field.type === 'date' && value) {
                        row[label] = new Date(value).toLocaleDateString(language === 'ar' ? 'ar-SY' : 'en-US');
                    } else {
                        row[label] = value !== undefined && value !== null && value !== '' ? value : '-';
                    }
                }
            });
        } else {
            // If no form fields provided, export all data fields
            Object.keys(submissionData).forEach(key => {
                const value = submissionData[key];
                if (typeof value === 'object' && value !== null) {
                    row[key] = JSON.stringify(value);
                } else {
                    row[key] = value || '-';
                }
            });
        }

        return row;
    });

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(data);

    // Auto-size columns
    const maxWidth = 50;
    const colWidths = Object.keys(data[0] || {}).map(key => {
        const maxLength = Math.max(
            key.length,
            ...data.map(row => String(row[key] || '').length)
        );
        return { wch: Math.min(maxLength + 2, maxWidth) };
    });
    worksheet['!cols'] = colWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, language === 'ar' ? 'التقديمات' : 'Submissions');

    // Generate filename
    const defaultFilename = `submissions_${new Date().toISOString().split('T')[0]}.xlsx`;
    const finalFilename = filename || defaultFilename;

    // Write file as proper xlsx using Blob to ensure correct Excel format
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

/**
 * Export pending approvals to Excel file
 * @param {Array} approvals - Array of pending approval objects
 * @param {string} language - Current language ('en' or 'ar')
 */
export const exportApprovalsToExcel = (approvals, language = 'en') => {
    const filename = `pending_approvals_${new Date().toISOString().split('T')[0]}.xlsx`;
    exportSubmissionsToExcel(approvals, [], language, filename);
};
